// Which state the profile-regen block dialog is in, given the reported failure kind
// (from the 409) and the live regen status polled off the org. Extracted as a pure
// function so the intricate ordering can be unit-tested without the dialog's
// effects/refs.
export type BlockPhase = 'needsinfo' | 'failedagain' | 'done' | 'updating'

export function deriveBlockPhase(opts: {
  // Failure was insufficient_data — must answer the wizard, no auto-retry.
  needsInfo: boolean
  // The retry we kicked off couldn't even start.
  retryError: boolean
  // orgs.profile_regen_status: 'idle' | 'in_progress' | 'failed' | undefined (loading).
  liveStatus: string | null | undefined
  // The auto-retry has been kicked off and its status refetched. Distinguishes the
  // initial 'failed' (pre-retry) from a failed *retry* — WITHOUT needing to observe
  // in_progress, which a regen failing within one poll window would skip.
  retrySettled: boolean
  // We've refetched the status since the 409 opened the dialog, so `liveStatus` is
  // fresh — not a cached 'idle' from before another tab/user started the regen.
  confirmedFresh: boolean
}): BlockPhase {
  const { needsInfo, retryError, liveStatus, retrySettled, confirmedFresh } = opts
  if (needsInfo) return 'needsinfo'
  // Failed retry once it's been kicked off + refetched (retrySettled); the initial
  // 'failed' before the retry starts reads as 'updating'. Not gated on observing
  // in_progress — a fast fail within one poll window never shows it.
  if (retryError || (liveStatus === 'failed' && retrySettled)) return 'failedagain'
  // Only conclude "done" on a fresh 'idle'. A cached 'idle' (before this tab knew a
  // regen started) must not open the dialog straight to "up to date".
  if (liveStatus === 'idle' && confirmedFresh) return 'done'
  return 'updating'
}
